'use client';

import { IconBook, IconBranch, IconFeather, IconNest, IconSparkle } from '@perch/icons';
import { PerchBird } from '@perch/mascot/react';
import { Badge, Row, Text } from '@perch/ui';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useState } from 'react';
import { hasCustomerSession } from '../authApi';
import { CommandPalette } from './CommandPalette';
import { NavLink } from './NavLink';

const UNAUTHENTICATED_PATHS = ['/', '/login', '/signup', '/forgot', '/reset'];

export function Shell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const bare =
    !pathname || UNAUTHENTICATED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const [gateChecked, setGateChecked] = useState(bare);

  useEffect(() => {
    if (bare) return;
    let cancelled = false;
    hasCustomerSession().then((ok) => {
      if (cancelled) return;
      if (ok) setGateChecked(true);
      else window.location.replace('/login/');
    });
    return () => {
      cancelled = true;
    };
  }, [bare]);

  if (bare) return <>{children}</>;
  if (!gateChecked) return null;
  return (
    <div className="shell">
      <CommandPalette />
      <header className="shell-header">
        <Row gap={3}>
          <PerchBird size={28} />
          <span className="perch-wordmark" style={{ fontSize: 20 }}>
            Perch
          </span>
          <Badge tone="dusk">acme.com</Badge>
        </Row>
        <Row gap={3}>
          <Text as="span" variant="body-s" tone="whisper">
            ⌘K
          </Text>
        </Row>
      </header>

      <aside className="shell-sidebar" aria-label="Primary navigation">
        <div className="nav-section">
          <span className="nav-section-label">Run</span>
          <NavLink href="/" icon={<IconNest />}>
            Home
          </NavLink>
          <NavLink href="/live" icon={<IconSparkle />}>
            Live
          </NavLink>
          <NavLink href="/conversations" icon={<IconFeather />}>
            Conversations
          </NavLink>
        </div>
        <div className="nav-section">
          <span className="nav-section-label">Shape</span>
          <NavLink href="/knowledge" icon={<IconBook />}>
            Knowledge
          </NavLink>
          <NavLink href="/agent" icon={<IconBranch />}>
            Agent
          </NavLink>
          <NavLink href="/bird" icon={<IconSparkle />}>
            Bird
          </NavLink>
        </div>
        <div className="nav-section">
          <span className="nav-section-label">Manage</span>
          <Link className="nav-item" href="/billing">
            Billing
          </Link>
          <Link className="nav-item" href="/team">
            Team
          </Link>
          <Link className="nav-item" href="/settings">
            Settings
          </Link>
        </div>
      </aside>

      <main className="shell-main">{children}</main>
    </div>
  );
}
